import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Paper,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Clear,
  Build,
  CalendarToday,
  AttachMoney,
  LocationOn
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { equipmentService } from '../services/equipmentService';
import api from '../services/api';

interface EquipmentFormData {
  name: string;
  type: string;
  model: string;
  serialNumber: string;
  manufacturer: string;
  purchaseDate: string;
  purchasePrice: string;
  currentValue: string;
  status: string;
  location: string;
  assignedTo: string;
  maintenanceSchedule: string;
  warrantyExpiry: string;
  notes: string;
}

const EquipmentCreate: React.FC = () => {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Array<{ _id: string; name: string }>>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [formData, setFormData] = useState<EquipmentFormData>({
    name: '',
    type: '',
    model: '',
    serialNumber: '',
    manufacturer: '',
    purchaseDate: '',
    purchasePrice: '',
    currentValue: '',
    status: 'Active',
    location: '',
    assignedTo: '',
    maintenanceSchedule: '',
    warrantyExpiry: '',
    notes: ''
  });

  const [errors, setErrors] = useState<Partial<EquipmentFormData>>({});
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Fetch current user role and company; if admin, fetch companies list
    const bootstrap = async () => {
      try {
        const res = await api.get('/auth/me');
        const user = res.data?.user || res.data;
        const role = user?.role;
        setUserRole(role || null);

        const company = user?.company;
        const id = typeof company === 'object' ? (company?._id || company?.id) : company;
        if (id) {
          setCompanyId(String(id));
          if (role !== 'admin') {
            setSelectedCompanyId(String(id));
          }
        }

        if (role === 'admin') {
          const list = await api.get('/companies');
          const data = list.data?.data ?? list.data ?? [];
          const mapped = Array.isArray(data)
            ? data.map((c: any) => ({ _id: String(c._id || c.id), name: String(c.name || '') }))
            : [];
          setCompanies(mapped);
        }
      } catch (err) {
        console.warn('Unable to bootstrap user/company for equipment create');
      }
    };
    bootstrap();
  }, []);

  const equipmentTypes = [
    'Excavator',
    'Bulldozer',
    'Crane',
    'Dump Truck',
    'Concrete Mixer',
    'Forklift',
    'Generator',
    'Compressor',
    'Welding Equipment',
    'Power Tools',
    'Safety Equipment',
    'Other'
  ];

  const statusOptions = [
    'Active',
    'Maintenance',
    'Out of Service',
    'Retired'
  ];

  const maintenanceSchedules = [
    'Weekly',
    'Monthly',
    'Quarterly',
    'Semi-Annual',
    'Annual',
    'As Needed'
  ];

  const handleInputChange = (field: keyof EquipmentFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<EquipmentFormData> = {};

    if (!formData.name.trim()) newErrors.name = 'Equipment name is required';
    if (!formData.type) newErrors.type = 'Equipment type is required';
    if (!formData.model.trim()) newErrors.model = 'Model is required';
    if (!formData.serialNumber.trim()) newErrors.serialNumber = 'Serial number is required';
    if (!formData.manufacturer.trim()) newErrors.manufacturer = 'Manufacturer is required';
    if (!formData.purchaseDate) newErrors.purchaseDate = 'Purchase date is required';
    if (!formData.purchasePrice.trim()) newErrors.purchasePrice = 'Purchase price is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';

    // Validate purchase price is a number
    if (formData.purchasePrice && isNaN(Number(formData.purchasePrice))) {
      newErrors.purchasePrice = 'Purchase price must be a valid number';
    }

    // Validate current value is a number if provided
    if (formData.currentValue && isNaN(Number(formData.currentValue))) {
      newErrors.currentValue = 'Current value must be a valid number';
    }

    // Validate company selection for admins
    if (userRole === 'admin') {
      if (!selectedCompanyId) {
        setCompanyError('Company is required for admin');
      } else {
        setCompanyError(null);
      }
    } else {
      setCompanyError(null);
    }

    setErrors(newErrors);
    const formValid = Object.keys(newErrors).length === 0;
    const companyValid = userRole === 'admin' ? !!selectedCompanyId : true;
    return formValid && companyValid;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      setSubmitStatus('error');
      return;
    }

    setSubmitStatus('loading');

    try {
      // Map frontend fields to backend schema
      const mapTypeToBackend = (type: string) => {
        switch (type) {
          case 'Dump Truck':
            return 'Vehicles';
          case 'Power Tools':
          case 'Welding Equipment':
            return 'Tools';
          case 'Safety Equipment':
            return 'Safety Equipment';
          case 'Excavator':
          case 'Bulldozer':
          case 'Crane':
          case 'Concrete Mixer':
          case 'Forklift':
          case 'Generator':
          case 'Compressor':
            return 'Heavy Machinery';
          default:
            return 'Other';
        }
      };

      const mapStatusToBackend = (status: string) => {
        switch (status) {
          case 'Active':
            return 'Available';
          case 'Maintenance':
            return 'Maintenance';
          case 'Out of Service':
            return 'Repair';
          case 'Retired':
            return 'Retired';
          default:
            return 'Available';
        }
      };

      const payload = {
        name: formData.name.trim(),
        type: mapTypeToBackend(formData.type),
        model: formData.model.trim(),
        serialNumber: formData.serialNumber.trim(),
        manufacturer: formData.manufacturer.trim(),
        purchaseDate: formData.purchaseDate ? new Date(formData.purchaseDate).toISOString() : undefined,
        purchasePrice: formData.purchasePrice ? Number(formData.purchasePrice) : undefined,
        currentValue: formData.currentValue ? Number(formData.currentValue) : undefined,
        status: mapStatusToBackend(formData.status),
        location: formData.location.trim(),
        // Include company based on role: admin selects, non-admin auto-populated
        ...((userRole === 'admin' && selectedCompanyId)
          ? { company: selectedCompanyId }
          : (companyId ? { company: companyId } : {}))
      };

      const created = await equipmentService.create(payload as any);
      if (created && created._id) {
        setSubmitStatus('success');
        // Navigate back to equipment list after successful creation
        setTimeout(() => {
          navigate('/equipment');
        }, 800);
      } else {
        throw new Error('Unexpected response from API');
      }
    } catch (error) {
      console.error('Error creating equipment:', error);
      setSubmitStatus('error');
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      type: '',
      model: '',
      serialNumber: '',
      manufacturer: '',
      purchaseDate: '',
      purchasePrice: '',
      currentValue: '',
      status: 'Active',
      location: '',
      assignedTo: '',
      maintenanceSchedule: '',
      warrantyExpiry: '',
      notes: ''
    });
    setErrors({});
    setSubmitStatus('idle');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'loading': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/equipment')} 
            sx={{ color: 'white' }}
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Add New Equipment
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Create a new equipment record for your construction project
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Status Alert */}
      {submitStatus !== 'idle' && (
        <Alert 
          severity={getStatusColor(submitStatus) as any}
          sx={{ mb: 3 }}
          onClose={() => setSubmitStatus('idle')}
        >
          {submitStatus === 'loading' && 'Creating equipment record...'}
          {submitStatus === 'success' && 'Equipment created successfully! Redirecting...'}
          {submitStatus === 'error' && 'Please fix the errors below and try again.'}
        </Alert>
      )}

      {/* Form */}
      <Card elevation={2}>
        <form onSubmit={handleSubmit}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'grid', gap: 4 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  {userRole === 'admin' ? (
                    <FormControl fullWidth error={!!companyError}>
                      <InputLabel>Company *</InputLabel>
                      <Select
                        value={selectedCompanyId}
                        onChange={(e) => setSelectedCompanyId(e.target.value)}
                        label="Company *"
                      >
                        {companies.map((c) => (
                          <MenuItem key={c._id} value={c._id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {companyError && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                          {companyError}
                        </Typography>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="Company"
                      value={companyId ? 'Company assigned automatically' : 'Company will be assigned automatically'}
                      disabled
                      helperText="Your company is auto-populated based on your account"
                    />
                  )}
                  <TextField
                    fullWidth
                    label="Equipment Name"
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Build color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl fullWidth error={!!errors.type}>
                    <InputLabel>Equipment Type *</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      label="Equipment Type *"
                    >
                      {equipmentTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.type && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.type}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Model"
                    value={formData.model}
                    onChange={handleInputChange('model')}
                    error={!!errors.model}
                    helperText={errors.model}
                    required
                  />

                  <TextField
                    fullWidth
                    label="Serial Number"
                    value={formData.serialNumber}
                    onChange={handleInputChange('serialNumber')}
                    error={!!errors.serialNumber}
                    helperText={errors.serialNumber}
                    required
                  />

                  <TextField
                    fullWidth
                    label="Manufacturer"
                    value={formData.manufacturer}
                    onChange={handleInputChange('manufacturer')}
                    error={!!errors.manufacturer}
                    helperText={errors.manufacturer}
                    required
                  />

                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      label="Status"
                    >
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          <Chip 
                            label={status} 
                            size="small" 
                            color={status === 'Active' ? 'success' : status === 'Maintenance' ? 'warning' : 'default'}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Location & Assignment */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Location & Assignment
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Location"
                    value={formData.location}
                    onChange={handleInputChange('location')}
                    error={!!errors.location}
                    helperText={errors.location}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Assigned To"
                    value={formData.assignedTo}
                    onChange={handleInputChange('assignedTo')}
                    placeholder="Employee or team name"
                  />
                </Box>
              </Box>

              {/* Financial Information */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Financial Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Purchase Date"
                    type="date"
                    value={formData.purchaseDate}
                    onChange={handleInputChange('purchaseDate')}
                    error={!!errors.purchaseDate}
                    helperText={errors.purchaseDate}
                    required
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Purchase Price"
                    value={formData.purchasePrice}
                    onChange={handleInputChange('purchasePrice')}
                    error={!!errors.purchasePrice}
                    helperText={errors.purchasePrice}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Current Value"
                    value={formData.currentValue}
                    onChange={handleInputChange('currentValue')}
                    error={!!errors.currentValue}
                    helperText={errors.currentValue}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoney color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>

              {/* Maintenance Information */}
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Maintenance Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Maintenance Schedule</InputLabel>
                    <Select
                      value={formData.maintenanceSchedule}
                      onChange={(e) => setFormData(prev => ({ ...prev, maintenanceSchedule: e.target.value }))}
                      label="Maintenance Schedule"
                    >
                      {maintenanceSchedules.map((schedule) => (
                        <MenuItem key={schedule} value={schedule}>
                          {schedule}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Warranty Expiry"
                    type="date"
                    value={formData.warrantyExpiry}
                    onChange={handleInputChange('warrantyExpiry')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  multiline
                  rows={4}
                  placeholder="Additional notes about the equipment..."
                  sx={{ mt: 3 }}
                />
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  startIcon={<Clear />}
                  disabled={submitStatus === 'loading'}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={submitStatus === 'loading'}
                >
                  {submitStatus === 'loading' ? 'Creating...' : 'Create Equipment'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </form>
      </Card>
    </Box>
  );
};

export default EquipmentCreate;